/**
 * @description
 * Table selector dropdown component for choosing which VSCode Lens table to stream from.
 * Displays available tables with descriptions.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date October 8, 2025
 * @see admin/src/frontend/components/vscode-lens/TableSelector.test.tsx
 */

import { ChevronDown, Database } from 'lucide-react';
import type { TableInfo } from '../../../types/vscode-lens';

interface TableSelectorProps {
  tables: TableInfo[];
  selectedTable: string | null;
  onSelect: (table: string) => void;
  disabled?: boolean;
}

/**
 * Table selector component
 */
export function TableSelector({ tables, selectedTable, onSelect, disabled }: TableSelectorProps) {
  const selectedTableInfo = tables.find(t => t.name === selectedTable);

  return (
    <div className="relative">
      <label className="block text-sm font-medium mb-2">
        Select Table
      </label>
      <div className="relative">
        <button
          onClick={() => !disabled && document.getElementById('table-dropdown')?.classList.toggle('hidden')}
          disabled={disabled}
          className={`w-full flex items-center justify-between rounded-md border border-input bg-white px-4 py-2.5 text-sm transition-colors ${
            disabled 
              ? 'cursor-not-allowed opacity-50' 
              : 'hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary'
          }`}
        >
          <div className="flex items-center gap-2">
            <Database className="h-4 w-4 text-muted-foreground" />
            <span>
              {selectedTableInfo ? selectedTableInfo.name : 'Select a table...'}
            </span>
          </div>
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </button>

        {/* Dropdown Menu */}
        <div
          id="table-dropdown"
          className="hidden absolute z-10 mt-1 w-full rounded-md border border-input bg-white shadow-lg"
        >
          <div className="py-1">
            {tables.map((table) => (
              <button
                key={table.name}
                onClick={() => {
                  onSelect(table.name);
                  document.getElementById('table-dropdown')?.classList.add('hidden');
                }}
                className={`w-full px-4 py-2.5 text-left text-sm transition-colors hover:bg-gray-50 ${
                  selectedTable === table.name ? 'bg-gray-100' : ''
                }`}
              >
                <div>
                  <div className="font-medium">{table.name}</div>
                  {table.description && (
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {table.description}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {selectedTableInfo?.description && (
        <p className="mt-1.5 text-xs text-muted-foreground">
          {selectedTableInfo.description}
        </p>
      )}
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */